import React, { useEffect, useRef, useState } from 'react';

type SignaturePadModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (dataUrl: string) => void;
  projectName?: string;
  stepLabel?: string;
  initialDataUrl?: string | null;
};

const PAD_BG = { r: 255, g: 249, b: 232 };

function isCanvasBlank(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return true;
  const { width, height } = canvas;
  const pixels = ctx.getImageData(0, 0, width, height).data;
  for (let i = 0; i < pixels.length; i += 4) {
    const a = pixels[i + 3];
    if (a === 0) continue;
    if (pixels[i] !== PAD_BG.r || pixels[i + 1] !== PAD_BG.g || pixels[i + 2] !== PAD_BG.b) {
      return false;
    }
  }
  return true;
}

const SignaturePadModal: React.FC<SignaturePadModalProps> = ({
  open,
  onClose,
  onConfirm,
  projectName,
  stepLabel = '3 / 3',
  initialDataUrl,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [hasStroke, setHasStroke] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const paintBackground = (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => {
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = `rgb(${PAD_BG.r}, ${PAD_BG.g}, ${PAD_BG.b})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  };

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const prev = canvas.toDataURL('image/png');
    const hadContent = !isCanvasBlank(canvas);

    const ratio = window.devicePixelRatio || 1;
    const width = wrap.clientWidth;
    const height = Math.max(220, Math.round(width * 0.55));

    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.4;
    ctx.strokeStyle = '#111827';
    paintBackground(canvas, ctx);

    if (hadContent || initialDataUrl) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
        setHasStroke(true);
      };
      img.src = hadContent ? prev : initialDataUrl || prev;
    } else {
      setHasStroke(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    setConsent(false);
    setError(null);
    const frame = window.requestAnimationFrame(() => {
      resizeCanvas();
      if (initialDataUrl) {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (canvas && ctx) {
          const img = new Image();
          img.onload = () => {
            const width = canvas.clientWidth;
            const height = canvas.clientHeight;
            paintBackground(canvas, ctx);
            ctx.drawImage(img, 0, 0, width, height);
            setHasStroke(true);
          };
          img.src = initialDataUrl;
        }
      } else {
        clearPad(false);
      }
    });

    const onResize = () => resizeCanvas();
    window.addEventListener('resize', onResize);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const getPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const point = getPoint(event);
    if (!canvas || !point) return;
    canvas.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    lastPointRef.current = point;
    setError(null);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const point = getPoint(event);
    const last = lastPointRef.current;
    if (!canvas || !ctx || !point || !last) return;

    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
    if (!hasStroke) setHasStroke(true);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (canvas?.hasPointerCapture(event.pointerId)) {
      canvas.releasePointerCapture(event.pointerId);
    }
    drawingRef.current = false;
    lastPointRef.current = null;
  };

  const clearPad = (resetConsent = true) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    paintBackground(canvas, ctx);
    setHasStroke(false);
    if (resetConsent) setConsent(false);
    setError(null);
  };

  const handleConfirm = () => {
    const canvas = canvasRef.current;
    if (!canvas || isCanvasBlank(canvas) || !hasStroke) {
      setError('서명을 입력해 주세요.');
      return;
    }
    if (!consent) {
      setError('서명 안내 내용에 동의해 주세요.');
      return;
    }
    onConfirm(canvas.toDataURL('image/png'));
  };

  if (!open) return null;

  return (
    <div className="signature-modal" role="dialog" aria-modal="true" aria-labelledby="signature-modal-title">
      <button type="button" className="signature-modal__backdrop" aria-label="닫기" onClick={onClose} />
      <div className="signature-modal__panel">
        <header className="signature-modal__header">
          <h2 id="signature-modal-title" className="signature-modal__title">
            서명 입력
          </h2>
          <span className="signature-modal__step">{stepLabel}</span>
        </header>

        <div className="signature-modal__body">
          <div className="signature-modal__toolbar">
            <span className="signature-modal__label">고객 서명 입력</span>
            <button type="button" className="signature-modal__clear" onClick={() => clearPad()}>
              다시작성
            </button>
          </div>

          <div className="signature-modal__canvas-wrap" ref={wrapRef}>
            <canvas
              ref={canvasRef}
              className="signature-modal__canvas"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onPointerLeave={handlePointerUp}
            />
          </div>

          <div className="signature-modal__notice">
            <ul>
              <li className="signature-modal__notice-emphasis">
                본 서명은
                {projectName ? ` 「${projectName}」 ` : ' 본 '}
                계약 내용에 동의함을 의미합니다.
              </li>
              <li>서명은 해당 계약의 전자서명 용도로만 사용됩니다.</li>
              <li>입력하신 서명은 계약 체결 목적 외로 별도 보관·활용되지 않습니다.</li>
              <li>
                「전자서명법」 및 「전자문서 및 전자거래 기본법」에 따라 전자서명은 자필 서명과
                동일한 법적 효력을 가집니다.
              </li>
            </ul>
          </div>

          <label className={`signature-modal__consent${consent ? ' signature-modal__consent--checked' : ''}`}>
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => {
                setConsent(e.target.checked);
                setError(null);
              }}
            />
            <span className="signature-modal__consent-box" aria-hidden="true">
              {consent && (
                <svg viewBox="0 0 12 10" aria-hidden="true">
                  <path
                    d="M1 5.5L4.5 9L11 1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            <span className="signature-modal__consent-text">
              위 서비스의 내용을 이해하였으며, 이에 동의합니다.
            </span>
          </label>

          {error && <p className="signature-modal__error">{error}</p>}
        </div>

        <footer className="signature-modal__footer">
          <button type="button" className="signature-modal__prev" onClick={onClose}>
            이전
          </button>
          <button type="button" className="signature-modal__next" onClick={handleConfirm}>
            다음
          </button>
        </footer>
      </div>
    </div>
  );
};

export default SignaturePadModal;
